import {
    ApiWrapper,
    SearchConfig,
    SearchQuery,
    SearchQueryResult,
    SearchResultCatProps,
    SearchLastQuery,
    SearchAdminConfig,
} from '../../types';
import { JBaseApi } from '../base';
import { OF } from '../../util/network';
import { combineFormat } from '../../util';
import { FormatExtension } from '../../types/extensions';

export class JSearchApi extends JBaseApi {
    /**
     * return a completed version of the current config, taking in account platform installed modules
     */
    public getConfig(): Promise<ApiWrapper<SearchConfig>> {
        return this.network.post({ o: 'search', f: 'getConfig' });
    }

    /**
     * Return a maximum of 8 results for a given text query, classed upon config categories
     * @param query string, words can be preceded by + and - for inclusive/exclusive search
     * @param signal: @AbortSignal to cancel the fetch
     */
    public proposition(
        { query, format }: { query: string; format?: 'list' | 'little' },
        signal?: AbortSignal,
    ): Promise<ApiWrapper<SearchResultCatProps[]>> {
        return this.network.post({ o: 'search', f: 'proposition', query, format: format || 'little' } as OF, signal);
    }

    /**
     * Return a page of results based on a text query and various filters.
     * All parameters are optional
     * @param query
     * @param query.keywords string, words can be preceded by + and - for inclusive/exclusive search (default *:*)
     * @param query.category text id of a category from config,if specified: restrict results to these category
     * @param query.facetCategories boolean, if true: count by categories are returned
     * @param query.filters array of @SearchQueryFilterModel @SearchQueryFilterModel.
     * @param query.page integer. default 1. index of returned page (start by 1)
     * @param query.limit integer. default 20. number of result by page
     * @param query.sort @SearchQuerySortModel default score then date
     * @param query.options @SearchQueryOption. extra possible options for the search
     * @param signal: @AbortSignal to cancel the fetch

     * // Todo add query.facets (to be specified first)
     */
    public searchQuery(
        query: SearchQuery,
        params?: { signal?: AbortSignal; formatExtension?: FormatExtension },
    ): Promise<ApiWrapper<SearchQueryResult>> {
        const { keywords, category, facetCategories, filters, page, limit, sort, options, format } = query;
        return this.network.post(
            {
                o: 'search',
                f: 'query',
                keywords,
                category,
                facetCategories,
                filters,
                page,
                limit,
                sort,
                options,
                format: combineFormat(format, params?.formatExtension),
            } as OF,
            params?.signal,
        );
    }

    /**
     * return the five last keywords typed in the searchbar by the user
     */
    public getLastQueries(): Promise<ApiWrapper<SearchLastQuery[]>> {
        return this.network.post({ o: 'search', f: 'getLastQueries' });
    }

    /**
     * return the current platform json search-config object
     */
    // FIXME when doing the search admin
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getAdminConfig(): Promise<ApiWrapper<SearchAdminConfig>> {
        return this.network.post({ o: 'search', f: 'getAdminConfig' });
    }

    /**
     * set the platform search-config to the given json object
     * @param config object. json search config
     */
    // FIXME when doing the search admin
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public setAdminConfig(config: any): Promise<ApiWrapper<any>> {
        return this.network.post({ o: 'search', f: 'setAdminConfig', config } as OF);
    }
}
